import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User, AuthState } from '../types'
import { UserRole } from '../types'
import { CognitoAuthService, type CognitoUser } from '../services/cognitoAuth'

export type PortalType = 'admin' | 'finance'

interface AuthStore extends AuthState {
  currentPortal: PortalType | null
  isReadOnlyMode: boolean
  signIn: (username: string, password: string, rememberMe?: boolean) => Promise<void>
  signOut: () => Promise<void>
  setLoading: (loading: boolean) => void
  updateUser: (user: Partial<User>) => void
  initializeAuth: () => Promise<void>
  refreshToken: () => Promise<void>
  setPortal: (portal: PortalType) => void
  getUserPortal: (cognitoUser: CognitoUser) => PortalType | null
}

// Convert CognitoUser to our User type
const mapCognitoUserToUser = (cognitoUser: CognitoUser): User => ({
  id: cognitoUser.id,
  email: cognitoUser.email,
  firstName: cognitoUser.given_name || cognitoUser.name?.split(' ')[0] || '',
  lastName: cognitoUser.family_name || cognitoUser.name?.split(' ')[1] || '',
  role: cognitoUser.username === 'test-compliance-user'
    ? UserRole.ADMIN
    : cognitoUser.groups?.includes('data-admin') || cognitoUser.groups?.includes('admin')
    ? UserRole.ADMIN
    : cognitoUser.groups?.includes('moderator')
    ? UserRole.MODERATOR
    : UserRole.USER,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
})

// Determine user's portal based on username and groups
const getUserPortal = (cognitoUser: CognitoUser): PortalType | null => {
  // Check for specific usernames first
  if (cognitoUser.username === 'test-compliance-user') {
    return 'admin'
  }

  // Then check groups
  if (cognitoUser.groups?.includes('data-admin') || cognitoUser.groups?.includes('admin')) {
    return 'admin'
  }
  if (cognitoUser.groups?.includes('finance-advisor') || cognitoUser.groups?.includes('finance')) {
    return 'finance'
  }
  return null
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
      currentPortal: null,
      isReadOnlyMode: false,

      setPortal: (portal: PortalType) => {
        set({ currentPortal: portal })
      },

      getUserPortal: (cognitoUser: CognitoUser): PortalType | null => {
        return getUserPortal(cognitoUser)
      },

      signIn: async (username: string, password: string, rememberMe: boolean = false) => {
        try {
          const { user: cognitoUser, tokens } = await CognitoAuthService.signIn(username, password, rememberMe)
          console.log('Cognito User:', cognitoUser)
          // Determine which portal the user should access
          const userPortal = getUserPortal(cognitoUser)

          if (!userPortal) {
            throw new Error('Access denied. This user does not have permission to access any portal.')
          }

          const user = mapCognitoUserToUser(cognitoUser)

          set({
            user,
            isAuthenticated: true,
            accessToken: tokens.accessToken,
            currentPortal: userPortal,
            isReadOnlyMode: cognitoUser.username === 'test-compliance-user',
          })
        } catch (error: any) {
          throw error
        }
      },

      signOut: async () => {
        set({ isLoading: true })
        try {
          await CognitoAuthService.signOut()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            accessToken: null,
            currentPortal: null,
            isReadOnlyMode: false,
          })
        } catch (error: any) {
          // Even if signOut fails, clear local state
          CognitoAuthService.clearAuthState()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            accessToken: null,
            currentPortal: null,
            isReadOnlyMode: false,
          })
          throw error
        }
      },

      setLoading: (isLoading: boolean) => {
        set({ isLoading })
      },

      updateUser: (userData: Partial<User>) => {
        const currentUser = get().user
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData },
          })
        }
      },

      initializeAuth: async () => {
        // Only try to restore session if we're not already authenticated
        if (get().isAuthenticated) return

        set({ isLoading: true })
        try {
          // First check if there are stored tokens
          const storedTokens = CognitoAuthService.getStoredTokens()
          if (!storedTokens) {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              accessToken: null,
              isReadOnlyMode: false,
            })
            return
          }

          // Validate token and refresh if expired
          const validToken = await CognitoAuthService.validateAndRefreshToken()
          if (!validToken) {
            // Token validation/refresh failed, clear state
            console.log('Token validation failed during initialization, clearing auth state')
            CognitoAuthService.clearAuthState()
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              accessToken: null,
              currentPortal: null,
              isReadOnlyMode: false,
            })
            return
          }

          // Try to get current session
          const session = await CognitoAuthService.getCurrentSession()
          if (session) {
            const userPortal = getUserPortal(session.user)
            if (userPortal) {
              const user = mapCognitoUserToUser(session.user)
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                accessToken: session.tokens.accessToken,
                currentPortal: userPortal,
                isReadOnlyMode: session.user.username === 'test-compliance-user',
              })
            } else {
              set({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                accessToken: null,
                currentPortal: null,
                isReadOnlyMode: false,
              })
            }
          } else {
            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              accessToken: null,
              currentPortal: null,
              isReadOnlyMode: false,
            })
          }
        } catch (error) {
          console.error('Failed to initialize auth:', error)
          // Clear any stored tokens if initialization fails
          CognitoAuthService.clearAuthState()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            accessToken: null,
            isReadOnlyMode: false,
          })
        }
      },

      refreshToken: async () => {
        try {
          const tokens = await CognitoAuthService.refreshSession()
          if (tokens) {
            set({ accessToken: tokens.accessToken })
          } else {
            // If refresh fails, sign out
            await get().signOut()
          }
        } catch (error) {
          console.error('Failed to refresh token:', error)
          await get().signOut()
        }
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        currentPortal: state.currentPortal,
        isReadOnlyMode: state.isReadOnlyMode,
      }),
    }
  )
)